import { encodeGcomeApFrame } from '../protocols/frame'
import { GCOME_AP_CMD } from '../protocols/cmd'
import type { BaseDevice } from '../../../runtime/BaseDevice'
import type { DeviceLightSink, LightFrameOrder, Rgb } from './types'
import { delay } from '../../../utils/TimeUtils'
import { ReportEvents } from '../../../events/ReportEvents'

/** GCOME_AP 灯效同步（0xdd）相关常量。 */
const CMD_LIGHTING_SYNC = GCOME_AP_CMD.lightingSyncDownload
/** 0xdd output report id（GCOME_AP 默认 0x00）。 */
const OUT_REPORT_ID = 0x00
/** 0xdd 单帧 AP payload 上限（导出给上层计算下发段序）。 */
export const FRAME_BYTES = 56 // 单帧 AP payload 上限
/**
 * 紧凑打包：变化字节聚成 ≤56 字节短包。
 * 关闭：固定 56 字节整段重发。web 灯光页主线程更忙、包间距更大时，短包更容易留下
 * 「本地 prev 已黑、固件仍显示自定义灯残色」的扫描线残影 / 中央脉冲异色；整段重发
 * 能覆盖同段邻键。周期性全量校准见 FULL_RESYNC_MS。
 */
const COMPACT_PACKING = false
const FRAME_INTERVAL_MS = 5 // 推送节流上限（约 200 FPS，实际受设备应答速度限制）
/** 真机 LED 亮度增益：算法强度普遍 <1，且灯珠亮度低于屏幕预览，下发前提升。
 *  1.8× 让亮部接近满亮（自带灯效水平），同时保留低亮度层次。 */
const BRIGHTNESS_BOOST = 1.8
/**
 * 退出律动的恢复延时（ms）。
 * 律动期间 0xdd 连续直发 + 0x01 快速通讯暂停态下，设备不响应常规停-等命令
 * （GetFunc/SetFunc 50ms 超时）；退出必须先等管道排空、结束快速通讯、再等固件
 * 切换回常规应答，恢复命令才不会因超时失败。
 */
const EXIT_DRAIN_MS = 60 // 停止推送后等最后一批 0xdd 帧被设备消化
const EXIT_FAST_END_SETTLE_MS = 120 // 0x02 结束后等固件恢复常规应答
const EXIT_RESTORE_RETRY_MS = 200 // 恢复失败后的重试间隔

/**
 * 从设备 manifest layout 推导本型号实际点亮的 LED 灯珠总数
 * （主灯 key + 空格等副灯 ledIndexes 的最大索引 + 1），按型号自适应，
 * 避免写死型号假设导致其他键盘漏发/多发段。
 */
export function resolveLedCount(device: BaseDevice): number {
  let max = -1
  for (const row of device.manifest?.layout?.keys ?? []) {
    for (const item of row) {
      if (typeof item.key === 'number') max = Math.max(max, item.key)
      for (const led of item.ledIndexes ?? []) max = Math.max(max, led)
    }
  }
  return max + 1
}

function getSendRawApCommand(
  device: BaseDevice,
):
  | ((req: {
      command: number
      address?: number
      data?: number[] | Uint8Array
      timeout?: number
      maxRetries?: number
    }) => Promise<unknown>)
  | null {
  const sendRawApCommand = (
    device as unknown as {
      sendRawApCommand?: (req: {
        command: number
        address?: number
        data?: number[] | Uint8Array
        timeout?: number
        maxRetries?: number
      }) => Promise<unknown>
    }
  ).sendRawApCommand
  return typeof sendRawApCommand === 'function' ? sendRawApCommand.bind(device) : null
}

/** 当前设备是否具备 GCOME_AP 音乐律动 0xdd 下发能力。 */
export function supportsDeviceLightSink(device: BaseDevice): boolean {
  return getSendRawApCommand(device) != null
}

/**
 * GCOME_AP 键盘灯效下发适配器（音乐律动优化版）。
 *
 * 把前端律动每帧输出的 `Map<ledIndex, Rgb>` 通过 **0xdd 灯效同步下载**直接写设备，
 * 走实时控制通道、不做读回，彻底避免 `setKeysColor`（SetLedDefine 读-差异-写）的
 * 大量 USB 往返，从而把真机帧率从几 FPS 提到接近推送上限。
 *
 * 下发策略：
 * - 本地维护 LED 缓冲，只写与上一帧发生变化的字节，不读回；
 * - 进入律动：先清空自定义灯定义区（SetLedDefine 全黑，并快照以便退出恢复），
 *   再切 GAME_MODE=0 → 0x01 → 0xdd；否则固件偶发回刷定义区会露出不固定键异色/扫描残影；
 * - 0xdd 必须顺序逐包发送（WebHID 并发 sendReport 会堵死，表现为全灯效卡死+残影）；
 * - 默认整段重发 + 周期性全量校准；中央脉冲每帧全量重写黑底；
 * - 帧内限流 + 保留最新待发帧，避免 HID 命令拥塞；
 * - 单帧失败忽略、下一帧继续，不中断律动循环。
 */
export function createDeviceLightSink(device: BaseDevice): DeviceLightSink {
  // 直接用底层 HID 通道顺序连发 0xdd 段：绕过 CommandQueue 的 10ms 指令间隔和
  // InputReportExchange 的逐段等应答，把「逐段刷新」的段间延迟压到 sendReport 本身开销
  // （<1ms）。这是官方网页端能做到「全屏同步」的关键——原生驱动段间延迟极小，
  // 而走 SDK sendRawApCommand 时每段被队列+等应答放大到 15~25ms，5 段即 75~125ms 可见分片。
  const channel = device.channel
  if (!getSendRawApCommand(device)) {
    throw new Error('createDeviceLightSink 仅支持 GCOME_AP 设备（缺少 sendRawApCommand）')
  }

  const getLighting = () => {
    const feature = (device.features as Record<string, unknown>).keyboardLighting as {
      getCurrentEffect(): Promise<number>
      setCurrentEffect(effect: number): Promise<void>
      setBacklightSwitch(enabled: boolean): Promise<void>
      getKeysColor(domain: unknown, index: number): Promise<Array<{ index: number; rgb?: Rgb }>>
      setKeysColor(
        domain: unknown,
        keyMap: Array<{ index: number; rgb: Rgb }>,
        index: number,
      ): Promise<unknown>
    }
    if (!feature) throw new Error('当前设备未挂载 keyboardLighting 能力')
    return feature
  }

  let enabled = true
  let customEnsured = false
  /** 进入律动前快照的自定义灯定义区；退出时写回，避免永久清掉用户自定义灯。 */
  let savedLedDefine: Array<{ index: number; rgb: Rgb }> | null = null
  /** 本型号实际点亮的 LED 灯珠数（由 manifest layout 推导，按型号自适应）。 */
  const ledCount = resolveLedCount(device)
  const rgbBytes = ledCount * 3
  // 初始标记全部未同步（0xff≠任意 RGB 字节），首次推送强制全量，
  // 避免「设备原有状态 ≠ 本地 prev」时差异写判断无变化而漏发。
  const prev = new Uint8Array(rgbBytes).fill(0xff)
  const next = new Uint8Array(rgbBytes) // 当前帧缓冲
  let inFlight = false
  let lastSendAt = 0
  /** 发送中到达的新帧：保留最新一帧，发送完成后立即补发，避免丢帧导致间隔不均。 */
  let pending: { frame: Map<number, Rgb>; order: LightFrameOrder } | null = null
  /** 诊断日志节流：每 2s 最多打一条，定位「未下发」断点。 */
  let lastDiagAt = 0
  function diag(message: string): void {
    const now = Date.now()
    if (now - lastDiagAt > 2000) {
      lastDiagAt = now
      console.warn('[deviceLightSink]', message)
    }
  }
  /** 发送统计（帧率 / 每帧段数），供 UI 诊断验证差异写效果。 */
  let statFrames = 0
  let statSegments = 0
  let statWindowAt = Date.now()
  let statFps = 0
  let statAvgSegments = 0
  function trackSend(segmentCount: number): void {
    statFrames++
    statSegments += segmentCount
    const now = Date.now()
    const span = now - statWindowAt
    if (span >= 1000) {
      statFps = Math.round((statFrames * 1000) / span)
      statAvgSegments = Math.round((statSegments / Math.max(1, statFrames)) * 10) / 10
      statFrames = 0
      statSegments = 0
      statWindowAt = now
    }
  }

  /** 0x01 / 0x02 只发不等待，与 0xdd 同路径；禁止走 sendRawApCommand 停-等。 */
  async function sendFastCommand(command: number): Promise<void> {
    const frame = encodeGcomeApFrame({ command, data: [] })
    await channel.sendOutputReport(OUT_REPORT_ID, frame.raw)
  }

  async function sendSyncPayload(address: number, bytes: number[]): Promise<void> {
    const segFrame = encodeGcomeApFrame({
      command: CMD_LIGHTING_SYNC,
      address,
      data: bytes,
    })
    await channel.sendOutputReport(OUT_REPORT_ID, segFrame.raw)
  }

  /** 整盘顺序下发当前 next 缓冲。必须逐包 await：WebHID 并发 sendReport 会堵死 HID。 */
  async function sendWholeBuffer(): Promise<void> {
    for (let off = 0; off < rgbBytes; off += FRAME_BYTES) {
      const len = Math.min(FRAME_BYTES, rgbBytes - off)
      await sendSyncPayload(off, Array.from(next.slice(off, off + len)))
    }
    prev.set(next)
  }

  /**
   * 在 0x01 之前把自定义灯定义区清黑。
   * GAME_MODE=0 下固件会偶发按定义区回刷个别键；若定义区仍是用户自定义的蓝/红/紫/绿，
   * 就会在中央脉冲圈外、扫描线间隙露出「不固定键」异色。清黑后回刷也是黑。
   * 必须在 0x01 前完成（停-等 SetLedDefine）；退出时写回快照。
   */
  async function snapshotAndClearLedDefine(): Promise<void> {
    const lighting = getLighting()
    try {
      const current = await lighting.getKeysColor(undefined, 0)
      savedLedDefine = current.map((item) => ({
        index: item.index,
        rgb: item.rgb ? { r: item.rgb.r, g: item.rgb.g, b: item.rgb.b } : { r: 0, g: 0, b: 0 },
      }))
    } catch {
      savedLedDefine = null
    }
    const black = Array.from({ length: 128 }, (_, i) => ({
      index: i,
      rgb: { r: 0, g: 0, b: 0 },
    }))
    await lighting.setKeysColor(undefined, black, 0).catch(() => undefined)
  }

  async function restoreLedDefineSnapshot(): Promise<void> {
    const snapshot = savedLedDefine
    savedLedDefine = null
    if (!snapshot?.length) return
    await getLighting()
      .setKeysColor(undefined, snapshot, 0)
      .catch(() => undefined)
  }

  /** 确保设备处于自定义逐键 RGB 模式（GAME_MODE=0），并进入快速通讯状态。 */
  async function ensureCustomMode(): Promise<void> {
    if (customEnsured) return
    const lighting = getLighting()
    // GetFunc / SetFunc / SetLedDefine 必须在 0x01 之前完成：快速通讯开始后停-等会 50ms 超时，
    // 还会和 0xdd 抢同一条 HID。
    // 先清定义区再切 GAME_MODE=0：切 0 瞬间固件按定义区点亮，清黑后进入就不会闪杂色。
    await snapshotAndClearLedDefine()
    try {
      const current = await lighting.getCurrentEffect()
      if (current !== 0) {
        // 切到自定义模式：GCOME_AP 实现会在 setCurrentEffect(0) 内把"律动前模式"保存到
        // APP 数据区（「背光/侧灯关闭前模式」bl，等价于背光关闭时的操作），
        // 退出律动时由 restoreBuiltinEffect 从 bl 恢复，而不是固定回光谱循环。
        await lighting.setCurrentEffect(0)
      }
    } catch {
      await lighting.setCurrentEffect(0).catch(() => undefined)
    }
    // 0x01 快速通讯开始：让设备暂停其它工作，最快速度处理灯效同步。只发不等待。
    await sendFastCommand(GCOME_AP_CMD.fastStart).catch(() => undefined)
    // 再整盘 0xdd 写黑，对齐实时同步层与定义区。
    next.fill(0)
    await sendWholeBuffer().catch(() => undefined)
    prev.fill(0xff)
    customEnsured = true
  }

  /**
   * 退出律动时恢复律动前的灯效（从 APP 数据区 bl 恢复，等价于背光开启），并结束快速通讯。
   *
   * 退出顺序必须是「先结束快速通讯、再恢复灯效」：律动期间 0xdd 连续直发且设备处于
   * 0x01 快速通讯暂停态，此刻发 GetFunc/SetFunc 等停-等命令会 50ms 超时（协议文档
   * 明确禁止 0x01 之后再走 sendRawApCommand）。因此：
   * 1) 先等最后一批 0xdd 帧被设备消化（管道排空）；
   * 2) 发送 0x02 结束快速通讯，设备恢复常规工作；
   * 3) 再等待固件状态切换完成（恢复常规应答需要时间）；
   * 4) 然后才发恢复命令；若仍超时，稍候重试一次。
   */
  async function restoreBuiltinEffect(): Promise<void> {
    await delay(EXIT_DRAIN_MS)
    await sendFastCommand(GCOME_AP_CMD.fastEnd).catch(() => undefined)
    await delay(EXIT_FAST_END_SETTLE_MS)
    // 仍在自定义模式时可写回定义区；再恢复板载灯效。
    await restoreLedDefineSnapshot()
    try {
      // setBacklightSwitch(true) 在自定义模式(0)下从 bl 恢复占用前的灯效，
      // 未记录时回退 1（光谱循环）；不再是固定写死光谱循环。
      await getLighting().setBacklightSwitch(true)
      await notifyEffectRestored()
    } catch {
      // 设备可能仍在恢复通讯状态：稍候重试一次，避免恢复失败停留在自定义模式
      await delay(EXIT_RESTORE_RETRY_MS)
      try {
        await getLighting().setBacklightSwitch(true)
        await notifyEffectRestored()
      } catch {
        /* 忽略恢复失败 */
      }
    }
  }

  /**
   * 恢复成功后上报灯效变化（EffectChanged），让 web 层灯光 UI 同步到恢复的模式。
   * 律动期间 UI 会停留在自定义模式(0)，而恢复写入是 host 侧操作、不会触发设备
   * 0xA3 主动上报，若不补发事件，界面会一直显示"灯光自定义"，直到手动重新读取。
   */
  async function notifyEffectRestored(): Promise<void> {
    try {
      const restored = await getLighting().getCurrentEffect()
      device.emit(ReportEvents.EffectChanged, restored)
    } catch {
      /* 忽略上报失败 */
    }
  }

  /** 亮度增益 + 软饱和：1.8× 增益会让高亮度通道（>141）一起 clamp 到 255，
   *  把相近的高亮通道压成同一个值、丢失渐变（如霓虹青绿通道 255 与 220）。
   *  改为 200 以上向 255 渐近压缩，保留高亮度通道间的相对层次。 */
  function boostByte(value: number): number {
    if (value <= 0) return 0
    const boosted = value * BRIGHTNESS_BOOST
    if (boosted <= 200) return Math.round(boosted)
    return Math.round(200 + 55 * (1 - Math.exp(-(boosted - 200) / 90)))
  }

  const pushFrame = async (
    frame: Map<number, Rgb>,
    order: LightFrameOrder = 'centerFirst',
  ): Promise<void> => {
    if (!enabled) {
      diag(`pushFrame 被调用但 sink 已禁用（enabled=${enabled}）`)
      return
    }
    const now = Date.now()
    if (inFlight || now - lastSendAt < FRAME_INTERVAL_MS) {
      // 上一帧仍在发送（或未到节流间隔）：保留最新帧，发送完成后立即补发，
      // 保证设备空闲时总是发最新状态，避免“丢中间帧”造成帧间隔不均、变化量累积。
      pending = { frame, order }
      return
    }
    inFlight = true
    lastSendAt = now
    try {
      await ensureCustomMode()

      // 当前帧缓冲默认全黑：未出现在 frame 里的键都应熄灭，
      // 避免上一帧颜色残留（变黑的键必须收到置 0）。
      next.fill(0)
      for (const [index, rgb] of frame) {
        if (!Number.isInteger(index) || index < 0 || index >= ledCount) continue
        const b = index * 3
        next[b] = boostByte(rgb.r)
        next[b + 1] = boostByte(rgb.g)
        next[b + 2] = boostByte(rgb.b)
      }

      // 每帧标脏 prev → 整盘顺序重发。
      // 仅差分时：①固件按定义区回刷的键（next=prev=0）永不纠正；②扫描线旧列熄灭包
      // 若被后续帧挤掉会留残影。定义区已在进入时清黑，整盘重发负责实时层对齐。
      prev.fill(0xff)

      // 分帧发送 0xdd，仅发与上一帧不同的字节（含“熄灭”字节）。
      // 两种打包策略：
      //  - COMPACT_PACKING=true（默认）：把变化字节聚成 ≤56 字节的短包、任意起始地址。
      //    协议帧 LEN=0~55、ADDR 任意，支持短包；窄光束效果（扫描线等）每帧只变
      //    少量键，但灯珠按物理布线散在各 56 字节段里，整段重发要 4~5 包/帧，
      //    紧凑打包压到 1~2 包 → 真机按报告数吞吐时帧率接近翻倍。
      //  - false：固定 56 字节整段重发（兼容旧行为，设备不认短包时回退）。
      // 下发顺序：
      //  - centerFirst（中央脉冲等中心聚集效果）：先发中央区域、向两侧扩展，
      //    让中心对称键在设备分段应用时更接近同时点亮；
      //  - linear（扫描线/波浪等全盘移动效果）：按 address 递增，匹配设备扫描
      //    方向，避免“新位置先亮、旧位置后灭”导致线断开/闪烁；
      //  - number[]（自定义段序，如 [3,0,4,1,2]）：按给定段索引顺序下发，
      //    用于中央脉冲——让视觉中轴键所在段优先，真机上中轴先亮、向两边补全。

      let anyFrameSent = false
      let sentCount = 0

      if (COMPACT_PACKING) {
        // 收集变化字节区间（区间内未变化字节保留 next 当前值填充）
        const packets: { addr: number; len: number }[] = []
        let i = 0
        while (i < rgbBytes) {
          if (prev[i] === next[i]) {
            i++
            continue
          }
          const addr = i
          let last = addr
          let j = addr
          while (j < rgbBytes && j - addr < FRAME_BYTES) {
            if (prev[j] !== next[j]) last = j
            j++
          }
          packets.push({ addr, len: last - addr + 1 })
          i = j
        }
        // 按段序语义排序短包
        if (Array.isArray(order)) {
          const segRank = new Map<number, number>()
          order.forEach((seg, rank) => {
            if (!segRank.has(seg)) segRank.set(seg, rank)
          })
          packets.sort((a, b) => {
            const sa = Math.floor(a.addr / FRAME_BYTES)
            const sb = Math.floor(b.addr / FRAME_BYTES)
            return (segRank.get(sa) ?? 999) - (segRank.get(sb) ?? 999)
          })
        } else if (order === 'linear') {
          packets.sort((a, b) => a.addr - b.addr)
        } else {
          const center = rgbBytes / 2
          packets.sort(
            (a, b) => Math.abs(a.addr + a.len / 2 - center) - Math.abs(b.addr + b.len / 2 - center),
          )
        }
        for (const p of packets) {
          await sendSyncPayload(p.addr, Array.from(next.slice(p.addr, p.addr + p.len)))
          anyFrameSent = true
          sentCount++
        }
      } else {
        const frameCount = Math.ceil(rgbBytes / FRAME_BYTES)
        const offsets: number[] = []
        if (Array.isArray(order)) {
          for (const seg of order) {
            if (Number.isInteger(seg) && seg >= 0 && seg < frameCount) {
              offsets.push(seg * FRAME_BYTES)
            }
          }
        } else if (order === 'linear') {
          for (let i = 0; i < frameCount; i++) offsets.push(i * FRAME_BYTES)
        } else {
          const mid = Math.floor(frameCount / 2)
          offsets.push(mid * FRAME_BYTES)
          for (let step = 1; step <= mid; step++) {
            if (mid - step >= 0) offsets.push((mid - step) * FRAME_BYTES)
            if (mid + step < frameCount) offsets.push((mid + step) * FRAME_BYTES)
          }
        }

        for (const off of offsets) {
          const len = Math.min(FRAME_BYTES, rgbBytes - off)
          let frameChanged = false
          for (let k = 0; k < len; k++) {
            if (prev[off + k] !== next[off + k]) {
              frameChanged = true
              break
            }
          }
          if (!frameChanged) continue

          await sendSyncPayload(off, Array.from(next.slice(off, off + len)))
          anyFrameSent = true
          sentCount++
        }
      }

      if (anyFrameSent) {
        prev.set(next)
        trackSend(sentCount)
      }
    } catch (err) {
      // 单帧下发失败忽略，避免中断律动循环；下一帧会继续尝试
      diag(`pushFrame 下发失败: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      inFlight = false
      // 发送完成：立即补发等待中的最新帧（队列长度 1，始终发最新状态）
      if (pending) {
        const queued = pending
        pending = null
        void Promise.resolve(pushFrame(queued.frame, queued.order)).catch(() => undefined)
      }
    }
  }

  return {
    get enabled() {
      return enabled
    },
    set enabled(value: boolean) {
      const wasEnabled = enabled
      enabled = value
      if (wasEnabled && !value) {
        void restoreBuiltinEffect()
      }
      if (!value) {
        customEnsured = false
        inFlight = false
        // savedLedDefine 由 restoreBuiltinEffect → restoreLedDefineSnapshot 写回后清空
        prev.fill(0xff)
      }
    },
    getStats() {
      return { fps: statFps, avgSegments: statAvgSegments }
    },
    pushFrame,
  }
}
